import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Home as HomeIcon,
  LocalOffer as LocalOfferIcon,
  ShoppingCart as ShoppingCartIcon,
  Timer as TimerIcon,
  Percent as PercentIcon,
  Discount as DiscountIcon
} from '@mui/icons-material';
import { getOffersPagePromotions, getOnSaleProducts } from '../../services/promotionService';
import { Promotion, DiscountType } from '../../types/promotion';
import { InventoryItem } from '../../utils/types';
import ProductCard from '../../components/store/ProductCard';
// تم إزالة استيراد مكتبة date-fns

// واجهة خصائص TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// مكون TabPanel
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`offers-tabpanel-${index}`}
      aria-labelledby={`offers-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const OffersPage: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // تحميل العروض والمنتجات
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // تحميل العروض
        const promotionsData = await getOffersPagePromotions();
        setPromotions(promotionsData);

        // تحميل المنتجات التي عليها تخفيضات
        const productsData = await getOnSaleProducts(20);
        setProducts(productsData);

        setLoading(false);
      } catch (err) {
        console.error('Error loading offers data:', err);
        setError('حدث خطأ أثناء تحميل بيانات العروض');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // تغيير التبويب
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // تنسيق تاريخ انتهاء العرض
  const formatExpiryDate = (date: Date | string) => {
    const expiryDate = typeof date === 'string' ? new Date(date) : date;

    // حساب الفرق بين التاريخين بالأيام
    const diffTime = expiryDate.getTime() - new Date().getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'منتهي';
    } else if (diffDays === 0) {
      return 'اليوم';
    } else if (diffDays === 1) {
      return 'غداً';
    } else if (diffDays <= 30) {
      return `بعد ${diffDays} يوم`;
    } else {
      // تنسيق التاريخ بالطريقة العربية
      return expiryDate.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // تنسيق نوع التخفيض
  const formatDiscountType = (promotion: Promotion) => {
    switch (promotion.discountType) {
      case DiscountType.PERCENTAGE:
        return `خصم ${promotion.discountValue.value}%`;
      case DiscountType.FIXED_AMOUNT:
        return `خصم ${promotion.discountValue.value} ج.م`;
      case DiscountType.BUY_X_GET_Y:
        if (promotion.buyXGetYDetails) {
          return `اشتر ${promotion.buyXGetYDetails.buyQuantity} واحصل على ${promotion.buyXGetYDetails.getQuantity} مجاناً`;
        }
        return 'عرض خاص';
      case DiscountType.BUNDLE:
        return 'عرض حزمة';
      default:
        return 'عرض خاص';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* فتات الخبز */}
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
          الرئيسية
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalOfferIcon sx={{ mr: 0.5, fontSize: 20 }} />
          العروض
        </Typography>
      </Breadcrumbs>

      {/* عنوان الصفحة */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <DiscountIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 35 }} />
          العروض والتخفيضات
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          استفد من أحدث العروض والتخفيضات على منتجاتنا
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : (
        <>
          {/* تبويبات العروض */}
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="offers tabs"
              centered
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab
                label="العروض الخاصة"
                id="offers-tab-0"
                icon={<LocalOfferIcon />}
                iconPosition="start"
              />
              <Tab
                label="منتجات بتخفيضات"
                id="offers-tab-1"
                icon={<PercentIcon />}
                iconPosition="start"
              />
            </Tabs>

            {/* محتوى تبويب العروض الخاصة */}
            <TabPanel value={tabValue} index={0}>
              {promotions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    لا توجد عروض خاصة حالياً
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {promotions.map((promotion) => (
                    <Grid item xs={12} sm={6} md={4} key={promotion.id}>
                      <Card
                        elevation={3}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        {promotion.imageUrl ? (
                          <CardMedia
                            component="img"
                            height="180"
                            image={promotion.imageUrl}
                            alt={promotion.title}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 180,
                              bgcolor: 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <DiscountIcon sx={{ fontSize: 80, color: 'white' }} />
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: 'error.main',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 'bold'
                          }}
                        >
                          {formatDiscountType(promotion)}
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h2" gutterBottom>
                            {promotion.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {promotion.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TimerIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              ينتهي {formatExpiryDate(promotion.endDate)}
                            </Typography>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            component={RouterLink}
                            to="/categories"
                          >
                            تسوق الآن
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* محتوى تبويب المنتجات بتخفيضات */}
            <TabPanel value={tabValue} index={1}>
              {products.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    لا توجد منتجات بتخفيضات حالياً
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                  {products.map((product) => (
                    <Grid item xs={6} sm={6} md={4} lg={3} key={product.id}>
                      <ProductCard product={product} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default OffersPage;
